import { Box, Button, TableBody, TableCell, TableRow } from '@mui/material'
import { ReactComponent as ArrowNext } from 'assets/fill_back_arrow.svg'
import { ReactComponent as UndigitizedIcon } from 'assets/Undigitized.svg'
import { COLORS } from 'styles/colors'
import TooltipHover from 'ui/TooltipHover'

import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'

import { IMilkingCowItem } from './IMilkingTableResponse'
import { SHIFT_LABELS } from '../../MilkYeildCard/constants'

interface YieldTableBodyProps {
	data: IMilkingCowItem[]
	undigitizedTags?: Set<string>
	undigitizedCollars?: Set<string>
}

const matchesUndigitized = (
	value: string | null | undefined,
	tags?: Set<string>,
	collars?: Set<string>,
) => {
	if (!value) return false
	const key = String(value)
	return Boolean(tags?.has(key) || collars?.has(key))
}

export const YieldTableBody = ({
	data,
	undigitizedTags,
	undigitizedCollars,
}: YieldTableBodyProps) => {
	const navigate = useNavigate()
	const navigateCowPasportPage = (
		tagId: string,
		id: number,
		identified?: boolean,
	) => {
		navigate(`/cattles/cow/${encodeURIComponent(String(tagId))}`, {
			state: { cowId: id, identified },
		})
		window.scrollTo(0, 0)
	}
	return (
		<TableBody
			sx={{
				'& td': {
					paddingTop: '12px',
					paddingBottom: '12px',
				},
			}}
		>
			{data.map(item => {
				const hasTagId = Boolean(item.tag_id?.trim())
				const isUndigitized =
					typeof item.identified === 'boolean'
						? item.identified === false
						: matchesUndigitized(
								item.tag_id,
								undigitizedTags,
								undigitizedCollars,
							) ||
							matchesUndigitized(
								item.collar_num,
								undigitizedTags,
								undigitizedCollars,
							)
				return (
					<TableRow
						key={`${item.id}-${item.last_milking_dt}`}
						sx={{ borderBottom: 'none' }}
					>
						<TableCell sx={{ borderBottom: 'none' }}>
							<Box display='flex' alignItems='center' gap='4px'>
								<TypographyPrimary
									sx={{
										color: isUndigitized ? COLORS.error : COLORS.black,
									}}
								>
									{hasTagId ? item.tag_id : '-'}
								</TypographyPrimary>
								{isUndigitized && (
									<TooltipHover
										title={
											<span
												style={{
													color: COLORS.error,
													fontWeight: 400,
												}}
											>
												Корова не оцифрована.
											</span>
										}
										placement='bottom'
										oneLine
										disableHoverBackground
									>
										<Box display='flex'>
											<UndigitizedIcon width={16} height={16} />
										</Box>
									</TooltipHover>
								)}
							</Box>
							{item.collar_num && (
								<TypographySecondary14>{item.collar_num}</TypographySecondary14>
							)}
						</TableCell>
						<TableCell sx={{ borderBottom: 'none' }}>
							<TypographyPrimary>
								{item.group ? item.group.title : '-'}
							</TypographyPrimary>
						</TableCell>
						<TableCell sx={{ borderBottom: 'none' }}>
							<TypographyPrimary>
								{item.today_milking_amount_sum
									? `${item.today_milking_amount_sum} л`
									: '-'}
							</TypographyPrimary>
						</TableCell>
						<TableCell sx={{ borderBottom: 'none' }}>
							<TypographySecondary>
								{item.last_milking_dt ? (
									<>
										{moment(item.last_milking_dt).format('HH:mm:ss')}
										<br />
										{moment(item.last_milking_dt).format('DD.MM.YYYY')}
									</>
								) : (
									'-'
								)}
							</TypographySecondary>
						</TableCell>
						<TableCell sx={{ borderBottom: 'none' }}>
							<TypographyPrimary>
								{item.period
									? (SHIFT_LABELS[item.period.toLowerCase()] ?? item.period)
									: '-'}
							</TypographyPrimary>
						</TableCell>
						<TableCell sx={{ borderBottom: 'none' }}>
							<TypographyPrimary>
								{item.milking_hall ? item.milking_hall : '-'}
							</TypographyPrimary>
						</TableCell>
						<TableCell sx={{ borderBottom: 'none' }}>
							{hasTagId ? (
								<Button
									disableRipple={true}
									sx={{
										'&:hover': { backgroundColor: 'transparent' },
									}}
									onClick={() =>
										navigateCowPasportPage(
											item.tag_id as string,
											item.id,
											isUndigitized ? false : item.identified,
										)
									}
								>
									<TooltipHover
										title='Перейти на страницу коровы'
										placement='bottom-start'
										oneLine
									>
										<ArrowNext
											style={{
												cursor: 'pointer',
												transform: 'rotate(180deg)',
											}}
											stroke='#9A9A9A'
										/>
									</TooltipHover>
								</Button>
							) : null}
						</TableCell>
					</TableRow>
				)
			})}
		</TableBody>
	)
}
